/**
 * Neo External Exposure Briefing card.
 *
 * Renders the most recent autonomous-sweep state for the active customer:
 *   - Last-run timestamp (relative) + per-source health chips.
 *   - Counts by severity + total un-acked.
 *   - Findings feed sorted critical → low; each row supports "Acknowledge".
 *   - Admin-only "Run now" button (rate-limited server-side to 3/min).
 *
 * Polls `GET /darkweb/agent/state` every 60s. Falls back to a friendly empty
 * state until the scheduler runs the first sweep (~30s after server boot or
 * on first admin-triggered "Run now").
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { apiFetch, ApiError } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';

type Severity = 'info' | 'low' | 'medium' | 'high' | 'critical';

interface Finding {
  id: string;
  customerId: string;
  source: string;
  fingerprint: string;
  target: string;
  kind: string;
  severity: Severity;
  summary: string;
  recommendedAction: string;
  indicators: string[] | null;
  evidence: Record<string, unknown> | null;
  firstSeen: string;
  lastSeen: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  /** GUID-style ARM resource name when the finding has been promoted to Sentinel; null otherwise. */
  sentinelIncidentArmId: string | null;
  /** Pre-built portal.azure.com deep-link to the Sentinel incident, when promoted. */
  sentinelIncidentUrl: string | null;
  promotedAt: string | null;
  createdAt: string;
}

interface SourceHealth { ok: boolean; error?: string; fetchedAt: string }

interface LastRunSummary {
  newCount: number;
  totalUnacked: number;
  bySeverity: Partial<Record<Severity, number>>;
  bySource: Record<string, number>;
  sourceHealth: Record<string, SourceHealth>;
  durationMs: number;
  promotedCount?: number;
  promotionFailures?: number;
}

interface AgentState {
  customerId: string;
  customerName: string;
  neoEnabled: boolean;
  monitoredDomainCount: number;
  lastRunAt: string | null;
  lastRunSummary: LastRunSummary | null;
  findings: Finding[];
  total: number;
  limit: number;
  offset: number;
  onlyUnacked: boolean;
}

const POLL_MS = 60_000;

const SEVERITY_ORDER: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

const SEVERITY_STYLES: Record<Severity, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 border border-red-300 dark:border-red-700',
  high:     'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200 border border-orange-300 dark:border-orange-700',
  medium:   'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border border-amber-300 dark:border-amber-700',
  low:      'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 border border-sky-300 dark:border-sky-700',
  info:     'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-300 dark:border-slate-600',
};

const SOURCE_LABEL: Record<string, string> = {
  hibp: 'Have I Been Pwned',
  hudsonRock: 'Hudson Rock',
  ahmia: 'Ahmia (Tor index)',
  ransomwareLive: 'Ransomware.live',
  urlscan: 'URLScan',
  github: 'GitHub Code Search',
  crtsh: 'crt.sh',
  shodan: 'Shodan',
  dnstwist: 'dnstwist (lookalikes)',
};

function relTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'unknown';
  const diff = Date.now() - t;
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

export function NeoBriefingCard(): ReactElement | null {
  const { activeCustomerId } = useCustomers();
  const { user, canWrite } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'developer';
  const showSourceHealth = user?.role === 'developer';
  // The read-only customer portal role cannot acknowledge findings server-side
  // (the route is gated by `blockCustomerViewer`). Hide the button so we
  // don't surface an action that would 403 — analysts and SOC L1/L2 still see it.
  // Server-side `requireTabWrite('darkweb')` gates ack & promote-to-incident.
  // Honor the per-customer matrix in addition to the legacy customer_viewer
  // exclusion so the buttons are inert for analysts denied write on this tenant.
  const canAck = user?.role !== 'customer_viewer' && canWrite('darkweb', activeCustomerId);

  const [state, setState] = useState<AgentState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [onlyUnacked, setOnlyUnacked] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [acking, setAcking] = useState<Set<string>>(new Set());
  const [promoting, setPromoting] = useState<Set<string>>(new Set());
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!activeCustomerId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        customerId: activeCustomerId,
        onlyUnacked: onlyUnacked ? 'true' : 'false',
        limit: '50',
      });
      const data = await apiFetch<AgentState>(`/darkweb/agent/state?${params.toString()}`);
      setState(data);
    } catch (e) {
      if (e instanceof ApiError && e.notAuthenticated) return; // global handler kicks in
      setError(e instanceof Error ? e.message : 'Failed to load Neo briefing');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeCustomerId, onlyUnacked]);

  // Initial load + when customer / filter changes.
  useEffect(() => { void load(false); }, [load]);

  // Background poll every 60s.
  useEffect(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    if (!activeCustomerId) return;
    pollTimer.current = setInterval(() => { void load(true); }, POLL_MS);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [activeCustomerId, load]);

  const runNow = useCallback(async () => {
    if (!activeCustomerId || !isAdmin) return;
    setRunning(true);
    setError(null);
    try {
      await apiFetch<{ started: boolean }>(`/darkweb/agent/run-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: activeCustomerId }),
      });
      // Sweep takes 10-60s; poll a few times so the user sees the new state
      // appear without waiting for the 60s background tick.
      setTimeout(() => { void load(true); }, 5_000);
      setTimeout(() => { void load(true); }, 15_000);
      setTimeout(() => { void load(true); }, 35_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start sweep');
    } finally {
      setRunning(false);
    }
  }, [activeCustomerId, isAdmin, load]);

  const ack = useCallback(async (id: string) => {
    if (!activeCustomerId) return;
    setAcking((s) => new Set(s).add(id));
    try {
      await apiFetch<{ ok: true }>(`/darkweb/agent/findings/${encodeURIComponent(id)}/ack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: activeCustomerId }),
      });
      // Optimistic — drop from list when filter is "only unacked".
      setState((prev) => {
        if (!prev) return prev;
        if (prev.onlyUnacked) {
          return { ...prev, findings: prev.findings.filter((f) => f.id !== id), total: Math.max(0, prev.total - 1) };
        }
        return {
          ...prev,
          findings: prev.findings.map((f) =>
            f.id === id ? { ...f, acknowledgedAt: new Date().toISOString(), acknowledgedBy: user?.email ?? 'me' } : f,
          ),
        };
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to acknowledge finding');
    } finally {
      setAcking((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  }, [activeCustomerId, user]);

  // Manually promote a finding to a Microsoft Sentinel incident. Hidden when
  // the finding already has `sentinelIncidentUrl` (we render the deep-link
  // instead) and hidden for the read-only customer portal role (server-side
  // gate in `blockCustomerViewer` would 403 either way). Idempotent on the
  // server, so a double-click is harmless.
  const promote = useCallback(async (id: string) => {
    if (!activeCustomerId) return;
    setPromoting((s) => new Set(s).add(id));
    try {
      const resp = await apiFetch<{
        ok: true;
        sentinelIncidentArmId: string;
        sentinelIncidentUrl: string;
      }>(`/darkweb/agent/findings/${encodeURIComponent(id)}/promote-to-incident`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: activeCustomerId }),
      });
      // Optimistically patch the row so the analyst sees the link without waiting for the next poll.
      setState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          findings: prev.findings.map((f) =>
            f.id === id
              ? {
                  ...f,
                  sentinelIncidentArmId: resp.sentinelIncidentArmId,
                  sentinelIncidentUrl: resp.sentinelIncidentUrl,
                  promotedAt: new Date().toISOString(),
                }
              : f,
          ),
        };
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to promote finding to a Sentinel incident');
    } finally {
      setPromoting((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  }, [activeCustomerId]);

  const sourceHealthEntries = useMemo(() => {
    const sh = state?.lastRunSummary?.sourceHealth ?? {};
    return Object.entries(sh).sort(([a], [b]) => a.localeCompare(b));
  }, [state?.lastRunSummary?.sourceHealth]);

  const sevCounts = state?.lastRunSummary?.bySeverity ?? {};

  if (!activeCustomerId) return null;

  return (
    <section
      data-testid="neo-briefing-card"
      className="rounded-lg border border-violet-200 dark:border-violet-800 bg-gradient-to-br from-violet-50 via-white to-indigo-50 dark:from-violet-950/40 dark:via-slate-900 dark:to-indigo-950/40 p-5 shadow-sm"
    >
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-violet-500" />
            Neo External Exposure Briefing
          </h2>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
            Checks 9 public threat-intel sources every 6 hours. Last sweep <span className="font-medium text-slate-900 dark:text-slate-200">{relTime(state?.lastRunAt)}</span>
            {state?.lastRunSummary?.durationMs ? `, took ${(state.lastRunSummary.durationMs / 1000).toFixed(1)}s` : ''}
            {state?.monitoredDomainCount != null ? `, across ${state.monitoredDomainCount} domain${state.monitoredDomainCount === 1 ? '' : 's'}` : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 cursor-pointer select-none">
            <input
              type="checkbox"
              className="rounded border-slate-300 dark:border-slate-600"
              checked={onlyUnacked}
              onChange={(e) => setOnlyUnacked(e.target.checked)}
            />
            Hide acknowledged
          </label>
          <button
            type="button"
            onClick={() => { void load(false); }}
            disabled={loading}
            className="px-2.5 py-1 rounded-md bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          {isAdmin ? (
            <button
              type="button"
              onClick={() => { void runNow(); }}
              disabled={running || (state?.monitoredDomainCount ?? 0) === 0}
              className="px-3 py-1 rounded-md bg-violet-600 hover:bg-violet-500 disabled:bg-slate-400 text-white text-xs font-medium"
              title={(state?.monitoredDomainCount ?? 0) === 0 ? 'Add a domain to the watch list first.' : 'Run a sweep right now without waiting for the schedule.'}
            >
              {running ? 'Starting…' : 'Run now'}
            </button>
          ) : null}
        </div>
      </div>

      {/* ── Severity counts row ──────────────────────────────── */}
      <div className="mt-4 flex flex-wrap gap-2">
        {SEVERITY_ORDER.map((s) => {
          const n = sevCounts[s] ?? 0;
          if (n === 0 && s !== 'critical' && s !== 'high') return null;
          return (
            <span key={s} className={`px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_STYLES[s]}`}>
              {s.toUpperCase()} {n}
            </span>
          );
        })}
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200">
          Open: {state?.lastRunSummary?.totalUnacked ?? 0}
        </span>
        {state?.lastRunSummary?.newCount ? (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200 border border-violet-300 dark:border-violet-700">
            {state.lastRunSummary.newCount} new this sweep
          </span>
        ) : null}
        {state?.lastRunSummary?.promotedCount ? (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 border border-blue-300 dark:border-blue-700"
            title="Findings the system opened as Microsoft Sentinel incidents during this sweep.">
            {state.lastRunSummary.promotedCount} sent to Sentinel
          </span>
        ) : null}
      </div>

      {/* ── Source health chips ──────────────────────────────── */}
      {showSourceHealth && sourceHealthEntries.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {sourceHealthEntries.map(([name, h]) => (
            <span
              key={name}
              title={h.error ? `Failed: ${h.error}` : `Working. Last checked ${relTime(h.fetchedAt)}.`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300"
            >
              <span
                aria-hidden
                className={`inline-block h-1.5 w-1.5 rounded-full ${h.ok ? 'bg-emerald-500' : 'bg-red-500'}`}
              />
              {SOURCE_LABEL[name] ?? name}
              {!h.ok && h.error ? (
                <span className="text-red-600 dark:text-red-400 truncate max-w-[160px]">· {h.error.slice(0, 60)}</span>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}

      {/* ── Error / loading / empty states ───────────────────── */}
      {error ? (
        <div className="mt-3 px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      ) : null}

      {!loading && !error && (state?.findings.length ?? 0) === 0 ? (
        <div className="mt-4 text-sm text-slate-600 dark:text-slate-400 italic">
          {state?.lastRunAt
            ? (onlyUnacked ? 'Nothing open right now. Every finding has been acknowledged.' : 'No findings on record yet.')
            : `Nothing yet. The first sweep runs shortly after startup, then every 6 hours.${isAdmin ? ' You can use Run now to start one immediately.' : ''}`}
        </div>
      ) : null}

      {/* ── Findings feed ────────────────────────────────────── */}
      {state && state.findings.length > 0 ? (
        <ul className="mt-4 space-y-2" data-testid="neo-findings-list">
          {state.findings.map((f) => {
            const isOpen = expanded.has(f.id);
            const isAcking = acking.has(f.id);
            return (
              <li
                key={f.id}
                data-testid={`neo-finding-${f.id}`}
                className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 p-3"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded font-medium ${SEVERITY_STYLES[f.severity]}`}>
                    {f.severity.toUpperCase()}
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-medium">
                    {SOURCE_LABEL[f.source] ?? f.source}
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    {f.kind}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 truncate">→ {f.target}</span>
                  <span className="ml-auto text-slate-500 dark:text-slate-400">
                    seen {relTime(f.lastSeen)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-900 dark:text-slate-100">{f.summary || '(no summary)'}</p>
                {f.recommendedAction ? (
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Suggested next step:</span>{' '}
                    {f.recommendedAction}
                  </p>
                ) : null}
                {(f.indicators?.length ?? 0) > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(f.indicators ?? []).slice(0, 6).map((ind, i) => (
                      <code
                        key={`${f.id}-ind-${i}`}
                        className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-900 text-[11px] text-slate-700 dark:text-slate-300 break-all max-w-full"
                      >
                        {ind.length > 90 ? ind.slice(0, 87) + '…' : ind}
                      </code>
                    ))}
                  </div>
                ) : null}
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setExpanded((s) => {
                      const next = new Set(s);
                      if (next.has(f.id)) next.delete(f.id); else next.add(f.id);
                      return next;
                    })}
                    className="text-xs text-violet-700 dark:text-violet-300 hover:underline"
                  >
                    {isOpen ? 'Hide raw evidence' : 'Show raw evidence'}
                  </button>
                  {/* Sentinel promote / link — wins over the acknowledge button on the right. */}
                  {f.sentinelIncidentUrl ? (
                    <a
                      href={f.sentinelIncidentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`neo-sentinel-${f.id}`}
                      className="text-xs text-blue-700 dark:text-blue-300 hover:underline inline-flex items-center gap-1"
                      title="Open the Sentinel incident this finding turned into."
                    >
                      ✓ Open Sentinel incident →
                    </a>
                  ) : canAck ? (
                    <button
                      type="button"
                      onClick={() => { void promote(f.id); }}
                      disabled={promoting.has(f.id)}
                      data-testid={`neo-promote-${f.id}`}
                      className="px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-500 disabled:bg-slate-400 text-white text-xs"
                      title="Open this finding as a Sentinel incident."
                    >
                      {promoting.has(f.id) ? 'Sending…' : 'Send to Sentinel'}
                    </button>
                  ) : null}
                  {f.acknowledgedAt ? (
                    <span className="text-xs text-emerald-700 dark:text-emerald-400">
                      ✓ Acknowledged by {f.acknowledgedBy ?? 'unknown'} · {relTime(f.acknowledgedAt)}
                    </span>
                  ) : canAck ? (
                    <button
                      type="button"
                      onClick={() => { void ack(f.id); }}
                      disabled={isAcking}
                      data-testid={`neo-ack-${f.id}`}
                      className="ml-auto px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white text-xs"
                    >
                      {isAcking ? 'Saving…' : 'Acknowledge'}
                    </button>
                  ) : null}
                </div>
                {isOpen ? (
                  <pre className="mt-2 text-[11px] bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 p-2 rounded border border-slate-200 dark:border-slate-700 overflow-x-auto max-h-64">
                    {JSON.stringify(f.evidence ?? {}, null, 2)}
                  </pre>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
